import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ChevronLeft, ChevronRight } from "lucide-react";

const MES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const DOW = ["L","M","X","J","V","S","D"];

function dayKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

export default function AvailabilityCalendar({ vehicleId }) {
  const [ranges, setRanges] = useState([]);
  const [gapHours, setGapHours] = useState(2);
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setDate(1); return d; });

  useEffect(() => {
    if (!vehicleId) return;
    axios.get(`/api/vehicles/${vehicleId}/disponibilidad`).then(r => {
      setRanges(r.data.ocupados || []);
      setGapHours(r.data.gap_hours || 2);
    }).catch(() => {});
  }, [vehicleId]);

  const occupied = useMemo(() => {
    const set = new Set();
    ranges.forEach(r => {
      try {
        const a = new Date(r.fecha_recogida);
        const b = new Date(r.fecha_devolucion);
        const cur = new Date(a.getFullYear(), a.getMonth(), a.getDate());
        const end = new Date(b.getFullYear(), b.getMonth(), b.getDate());
        while (cur <= end) {
          set.add(dayKey(cur));
          cur.setDate(cur.getDate()+1);
        }
      } catch { /* ignore */ }
    });
    return set;
  }, [ranges]);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const first = new Date(year, month, 1);
  const startDow = (first.getDay() + 6) % 7; // shift Sun->6 so Mon=0
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

  const prev = () => setCursor(new Date(year, month - 1, 1));
  const next = () => setCursor(new Date(year, month + 1, 1));
  const today = new Date(); today.setHours(0,0,0,0);

  return (
    <div className="max-w-xs mx-auto text-sm p-2 bg-white border border-neutral-200 rounded-lg" data-testid="availability-calendar">
      <div className="flex items-center justify-between mb-2">
        <button onClick={prev} className="p-0.5 border border-neutral-300 rounded hover:border-[var(--pc-red)]" data-testid="cal-prev"><ChevronLeft size={14} /></button>
        <div className="font-display text-sm" data-testid="cal-month-label">{MES[month]} {year}</div>
        <button onClick={next} className="p-0.5 border border-neutral-300 rounded hover:border-[var(--pc-red)]" data-testid="cal-next"><ChevronRight size={14} /></button>
      </div>
      <div className="grid grid-cols-7 gap-0.5 text-center text-[10px] text-neutral-500 mb-1">
        {DOW.map(d => <div key={d}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-0.5 text-xs">
        {cells.map((c, i) => {
          if (!c) return <div key={`e-${i}`}></div>;
          const k = dayKey(c);
          const isOcc = occupied.has(k);
          const isPast = c < today;
          const cls = isOcc
            ? "bg-red-100 text-red-700 border border-red-300 line-through"
            : isPast ? "text-neutral-300" : "bg-neutral-50 text-neutral-800 hover:bg-neutral-100";
          return (
            <div key={k}
                 data-testid={`cal-day-${k}${isOcc?"-busy":""}`}
                 className={`aspect-square flex items-center justify-center rounded text-[11px] ${cls}`}>
              {c.getDate()}
            </div>
          );
        })}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-neutral-600">
        <div className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 bg-red-100 border border-red-300 rounded"></span> Ocupado</div>
        <div className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 bg-neutral-50 border border-neutral-300 rounded"></span> Libre</div>
        <div className="ml-auto">Margen: {gapHours}h</div>
      </div>
    </div>
  );
}
